import { useMemo } from 'react';

import { motion } from 'framer-motion';

export type AnimationType =
  | 'leftToRight'
  | 'bottomToUp'
  | 'rightToLeft'
  | 'upToBottom';
interface MotionWrapperProps {
  children?: React.ReactNode;
  animationType?: AnimationType;
  overflowHidden?: boolean;
}

const pageTransition = {
  ease: 'easeInOut',
  duration: 0.4,
};

const bottomToUpVariants = {
  initial: {
    opacity: 0,
    y: 100,
    transition: pageTransition,
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: pageTransition,
  },
  exit: {
    opacity: 0,
    y: -100,
    transition: pageTransition,
  },
};

const upToBottomVariants = {
  initial: {
    opacity: 0,
    y: 100,
    transition: pageTransition,
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: pageTransition,
  },
  exit: {
    opacity: 0,
    y: -100,
    transition: pageTransition,
  },
};

const leftToRightVariants = {
  initial: {
    opacity: 0,
    x: 100,
    transition: pageTransition,
  },
  enter: {
    opacity: 1,
    x: 0,
    transition: pageTransition,
  },
  exit: {
    opacity: 0,
    x: -100,
    transition: pageTransition,
  },
};

const rightToLeftVariants = {
  initial: {
    opacity: 0,
    x: -100,
    transition: pageTransition,
  },
  enter: {
    opacity: 1,
    x: 0,
    transition: pageTransition,
  },
  exit: {
    opacity: 0,
    x: 100,
    transition: pageTransition,
  },
};

export function MotionWrapper({
  children,
  overflowHidden,
  animationType = 'rightToLeft',
}: MotionWrapperProps): JSX.Element {
  const variants = useMemo(() => {
    const animation = {
      leftToRight: leftToRightVariants,
      rightToLeft: rightToLeftVariants,
      bottomToUp: bottomToUpVariants,
      upToBottom: upToBottomVariants,
    };

    return animation[animationType] ?? leftToRightVariants;
  }, [animationType]);

  return (
    <motion.div
      style={{
        position: 'absolute',
        width: '100%',
        maxWidth: '100%',
        maxHeight: '100vh',
        minHeight: 'calc(100vh - 225px)',
        top: 0,
        left: 0,
        overflowY: overflowHidden ? 'hidden' : 'auto',
      }}
      initial="initial"
      animate="enter"
      exit="exit"
      variants={variants}
      transition={pageTransition}
    >
      {children}
    </motion.div>
  );
}
